import React, { useState, useContext, useRef } from 'react';
import { uploadFiles } from '../../../../api/file_uploads';
import { v4 as uuidv4 } from 'uuid';
import { failToast } from '../../../ui_kit/utilities/fail_toast';
import { isFeatureEnabled, FEATURES } from '../../../../helpers/feature_checker';

import LoadingScreen from '../../../system/loading_screen';
import Context from '../../../../utils/context';

import styles from './styles.module.css';

export default function S3UploadFile({
  onChange = () => {},
  onError = () => {},
  multiple = true,
  cta = 'Add photos',
  type = 'button',
}) {
  const context = useContext(Context);
  const currentUser = context.auth.getCurrentUser();
  const currentAccount = currentUser?.account;
  const allFiles = [];
  const allFileMetadata = [];
  const [loading, setLoading] = useState(false);
  const chatFileInputRef = useRef(null);
  const isAttachmentsHidden = isFeatureEnabled(currentAccount, FEATURES.DISALLOW_ATTACHMENTS_IN_MESSAGES);

  const s3UploadFile = (files, i) => {
    setLoading(true);

    // Check if we've processed all files
    if (files.length === i) {
      setLoading(false);
      onChange(allFiles, allFileMetadata);
      return;
    }

    const file = files[i];
    const extension = file.name.split('.').pop();

    // If file size is greater than 25MB, show error and stop processing
    if (file.size > 25 * 1024 * 1024) {
      setLoading(false);
      failToast('File size must be less than 25MB');
      onError();
      return;
    }

    // If invalid extension, show error and stop processing
    if (!extension) {
      setLoading(false);
      failToast('Invalid file extension, please try again.');
      onError();
      return;
    }

    const filename = uuidv4() + `-${currentUser.id}.${extension}`;

    uploadFiles(files[i], filename, (data) => {
      allFiles.push(data.url);
      allFileMetadata.push({ url: data.url, name: file.name });
      s3UploadFile(files, i + 1);
    });
  };

  const onFileChange = (e) => {
    s3UploadFile(e.target.files, 0);
  };

  return (
    <>
      {type === 'button' && (
        <div style={{ display: 'flex' }}>
          <button className={styles.file_btn}>
            <label className={styles.btn_txt}>
              {cta} <span className={styles.helper_txt}>(25mb max)</span>
              <img
                className={styles.icon}
                src='https://patientpartner-images2.s3.us-west-1.amazonaws.com/website/Vector.svg'
              />
              <input
                onChange={onFileChange}
                type='file'
                style={{ display: 'none' }}
                name='image'
                multiple={multiple}
                accept='.jpg,.jpeg,.png'
              />
            </label>
          </button>
          {loading && (
            <div style={{ margin: '10px 0px 0px 10px' }}>
              <LoadingScreen size={'22'} />
            </div>
          )}
        </div>
      )}
      {!isAttachmentsHidden && type === 'icon' && (
        <div style={{ display: 'flex' }}>
          {!loading ? (
            <button className={`${styles.attachment_btn}`}>
              <label>
                <img
                  className={styles.icon_btn}
                  src='https://patientpartner-images2.s3.us-west-1.amazonaws.com/website/platform/image.svg'
                />
                <input
                  onChange={onFileChange}
                  type='file'
                  style={{ display: 'none' }}
                  name='image'
                  multiple
                  accept='.jpg,.jpeg,.png'
                />
              </label>
            </button>
          ) : (
            <div style={{ margin: '10px 0px 0px 10px' }}>
              <LoadingScreen size={'22'} />
            </div>
          )}
        </div>
      )}
      {!isAttachmentsHidden && type === 'chat_icon' && (
        <div className={styles.chat_attachment_wrap}>
          <input
            ref={chatFileInputRef}
            onChange={onFileChange}
            type='file'
            className={styles.chat_attachment_input}
            name='image'
            multiple={multiple}
            accept='.jpg,.jpeg,.png'
          />
          <button
            type='button'
            className={styles.chat_attachment_button}
            onClick={() => chatFileInputRef.current?.click()}
            disabled={loading}
            aria-label='Attach a file'
          >
            {!loading ? (
              <svg
                width='20'
                height='20'
                viewBox='0 0 256 256'
                fill='currentColor'
                xmlns='http://www.w3.org/2000/svg'
                aria-hidden='true'
              >
                <path d='M212.48,136.49l-82.06,82a60,60,0,0,1-84.85-84.88l98.16-97.89a40,40,0,0,1,56.56,56.59l-.17.16-95.8,92.22a12,12,0,1,1-16.64-17.3l95.71-92.12a16,16,0,0,0-22.7-22.56L62.53,150.57a36,36,0,0,0,50.93,50.91l82.06-82a12,12,0,0,1,17,17Z' />
              </svg>
            ) : (
              <LoadingScreen size='20' />
            )}
          </button>
        </div>
      )}
      {type === 'field' && (
        <button style={{ position: 'relative' }} className={`${styles.drop_field}`}>
          <label>
            <p style={{ textAlign: 'left' }}>
              <span style={{ color: '#419DA5' }}>+ Add .csv</span> or drop file here
            </p>
            <input onChange={(e) => onFileChange(e)} type='file' name='file' accept='.csv' />
          </label>
        </button>
      )}
    </>
  );
}
